import { motion } from 'framer-motion';
import { useState } from 'react';

interface CaseTypeBarChartProps {
  data: { [key: string]: number };
  title: string;
  emptyDescription?: string;
}

// Modern gradient system - Blue/Purple shades with better visibility for low percentages
const CHART_GRADIENT_COLORS = [
  '#93C5FD', // Medium blue (lowest percentages) - more visible than light blue
  '#74A9F7', // Soft medium blue
  '#60A5FA', // Bright blue
  '#4F94F4', // Enhanced blue
  '#3B82F6', // Primary blue
  '#2563EB', // Deep blue
  '#1D4ED8', // Rich blue
  '#1E40AF', // Dark blue (highest percentages)
];

const getTypeColor = (percentage: number): string => {
  // Map percentage to color intensity (higher percentage = darker color)
  let colorIndex;

  if (percentage >= 50) {
    colorIndex = 7; // Bright golden for very high percentages
  } else if (percentage >= 30) {
    colorIndex = 6;
  } else if (percentage >= 20) {
    colorIndex = 5;
  } else if (percentage >= 10) {
    colorIndex = 4;
  } else if (percentage >= 5) {
    colorIndex = 3;
  } else if (percentage >= 2) {
    colorIndex = 2;
  } else if (percentage >= 1) {
    colorIndex = 1;
  } else {
    colorIndex = 0; // Lightest blue for very low percentages
  }

  return CHART_GRADIENT_COLORS[colorIndex];
};

interface TooltipData {
  type: string;
  count: number;
  percentage: number;
  x: number;
  y: number;
}

const CaseTypeBarChart = ({ data, title, emptyDescription }: CaseTypeBarChartProps) => {
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const total = Object.values(data).reduce((sum, value) => sum + value, 0);

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400 text-center">
          <div className="text-lg font-medium mb-2">No Data Available</div>
          <div className="text-sm">
            {emptyDescription || 'No case type data found for the selected date range'}
          </div>
        </div>
      </div>
    );
  }

  // Calculate segments and sort by value
  const segments = Object.entries(data)
    .filter(([_, value]) => value > 0)
    .map(([type, value]) => ({
      type,
      value,
      percentage: (value / total) * 100,
    }))
    .sort((a, b) => b.value - a.value);

  const maxValue = Math.max(...segments.map(s => s.value));

  // Fixed precise bar center calculation with absolute positioning
  const handleBarHover = (
    segment: { type: string; value: number; percentage: number },
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    const rowElement = event.currentTarget;

    // Find the bar background container (updated selector for h-12)
    const barBackground = rowElement.querySelector('.relative.h-12') as HTMLElement;

    if (barBackground) {
      const barBgRect = barBackground.getBoundingClientRect();

      // Calculate the actual bar width percentage
      const barWidthPercent = (segment.value / maxValue) * 100;

      // Calculate the actual pixel width of the colored portion
      const actualBarWidth = (barBgRect.width * barWidthPercent) / 100;

      // Calculate the center of the colored bar using viewport coordinates (for fixed positioning)
      const barCenterX = barBgRect.left + actualBarWidth / 2;
      const barTopY = barBgRect.top;

      setTooltip({
        type: segment.type,
        count: segment.value,
        percentage: segment.percentage,
        x: barCenterX,
        y: barTopY,
      });
    }

    setHoveredBar(segment.type);
  };

  const handleBarLeave = () => {
    setTooltip(null);
    setHoveredBar(null);
  };

  return (
    <motion.div
      className="flex flex-col h-full case-type-chart relative"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <div className="flex items-center justify-center gap-3 mb-3">
          <motion.div
            className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 flex items-center justify-center shadow-lg"
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </motion.div>
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent">
            {title}
          </h3>
        </div>
        <motion.div
          className="w-32 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full mx-auto"
          initial={{ width: 0 }}
          animate={{ width: 128 }}
          transition={{ delay: 0.5, duration: 0.8, ease: 'easeOut' }}
        />
        <div className="flex items-center justify-center gap-2 mt-3">
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
          <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Live Data</span>
          <div
            className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"
            style={{ animationDelay: '0.5s' }}
          ></div>
        </div>
      </motion.div>

      <div className="flex-1 flex items-center gap-8">
        {/* Total Cases Display - Left Side */}
        <motion.div
          className="flex-shrink-0"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="text-center relative">
            {/* Enhanced header with icon */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
              <div className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                Total Cases
              </div>
            </div>

            {/* Enhanced total cases card */}
            <motion.div
              className="relative inline-flex items-center justify-center w-28 h-28 rounded-3xl text-xl font-bold shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-3xl cursor-default overflow-hidden"
              style={{
                background:
                  'linear-gradient(135deg, #3B82F6 0%, #2563EB 30%, #1D4ED8 70%, #1E40AF 100%)',
                boxShadow:
                  '0 20px 40px rgba(59, 130, 246, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
              }}
              whileHover={{
                scale: 1.08,
                y: -4,
                rotate: 2,
                boxShadow:
                  '0 30px 60px rgba(59, 130, 246, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.4)',
                transition: { duration: 0.3, type: 'spring', stiffness: 300 },
              }}
              whileTap={{ scale: 0.95 }}
              initial={{ scale: 0, rotateY: -180 }}
              animate={{ scale: 1, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              {/* Animated background patterns */}
              <motion.div
                className="absolute inset-0"
                animate={{
                  background: [
                    'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 50%)',
                    'radial-gradient(circle at 70% 70%, rgba(255,255,255,0.2) 0%, transparent 50%)',
                    'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 50%)',
                  ],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />

              {/* Floating particles effect */}
              <div className="absolute inset-0">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-white/40 rounded-full"
                    style={{
                      left: `${20 + i * 25}%`,
                      top: `${15 + i * 20}%`,
                    }}
                    animate={{
                      y: [-2, 2, -2],
                      opacity: [0.3, 0.7, 0.3],
                    }}
                    transition={{
                      duration: 2 + i * 0.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: i * 0.3,
                    }}
                  />
                ))}
              </div>

              <motion.span
                className="leading-tight text-white relative z-10 drop-shadow-lg font-extrabold"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
              >
                {total.toLocaleString()}
              </motion.span>

              {/* Enhanced inner effects */}
              <div className="absolute inset-2 rounded-2xl bg-gradient-to-br from-white/15 via-transparent to-black/10 pointer-events-none" />
              <div className="absolute top-2 left-2 w-3 h-3 bg-white/20 rounded-full blur-sm" />
            </motion.div>

            {/* Enhanced footer with more info */}
            <motion.div
              className="mt-4 space-y-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.8 }}
            >
              <div className="text-xs text-gray-600 dark:text-gray-300 font-semibold tracking-wide">
                {segments.length} type{segments.length !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center justify-center gap-1">
                <div className="w-1 h-1 bg-green-400 rounded-full"></div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Active</div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Case Type Bars - Right Side */}
        <div className="flex-1 space-y-3">
          {segments.map((segment, index) => {
            const barWidth = (segment.value / maxValue) * 100;
            const percentage = (segment.value / total) * 100;
            const color = getTypeColor(percentage);
            const isHovered = hoveredBar === segment.type;

            return (
              <motion.div
                key={segment.type}
                className="relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                onMouseEnter={e => handleBarHover(segment, e)}
                onMouseLeave={handleBarLeave}
              >
                {/* Enhanced Premium Bar container */}
                <div className="relative h-12 bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-2xl overflow-hidden shadow-inner border border-gray-200/50 dark:border-gray-600/50 backdrop-blur-sm">
                  {/* Enhanced animated premium bar */}
                  <motion.div
                    className="h-full rounded-2xl shadow-lg relative overflow-hidden actual-bar"
                    style={{
                      background: `linear-gradient(135deg, ${color}f0 0%, ${color} 30%, ${color}ee 70%, ${color}dd 100%)`,
                      boxShadow: `0 4px 15px ${color}40, inset 0 2px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.1)`,
                    }}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{
                      width: `${barWidth}%`,
                      opacity: 1,
                      filter: isHovered
                        ? 'brightness(1.2) saturate(1.2) contrast(1.1)'
                        : 'brightness(1) saturate(1) contrast(1)',
                      boxShadow: isHovered
                        ? `0 8px 25px ${color}60, inset 0 3px 0 rgba(255,255,255,0.4), inset 0 -2px 0 rgba(0,0,0,0.15)`
                        : `0 4px 15px ${color}40, inset 0 2px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.1)`,
                    }}
                    transition={{
                      delay: 0.5 + index * 0.1,
                      duration: 1.2,
                      ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for smooth animation
                    }}
                    whileHover={{
                      scaleY: 1.15,
                      scaleX: 1.02,
                      filter: 'brightness(1.25) saturate(1.25) contrast(1.15)',
                      boxShadow: `0 12px 35px ${color}70, inset 0 4px 0 rgba(255,255,255,0.5), inset 0 -2px 0 rgba(0,0,0,0.2)`,
                      transition: { duration: 0.3, type: 'spring', stiffness: 400, damping: 25 },
                    }}
                  >
                    {/* Enhanced shimmer effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                      animate={{
                        x: ['-150%', '150%'],
                      }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        repeatDelay: 4,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                    />

                    {/* Pulse effect for high-value bars */}
                    {percentage > 20 && (
                      <motion.div
                        className="absolute inset-0 bg-white/10 rounded-2xl"
                        animate={{
                          opacity: [0, 0.3, 0],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      />
                    )}

                    {/* Enhanced inner highlights */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/25 via-transparent to-black/15 rounded-2xl" />
                    <div className="absolute top-1 left-1 right-1 h-2 bg-gradient-to-r from-white/30 via-white/20 to-transparent rounded-t-xl" />
                  </motion.div>

                  {/* Enhanced Label and value overlay */}
                  <div className="absolute inset-0 flex items-center justify-between px-5 pointer-events-none">
                    <div className="flex items-center gap-3">
                      {/* Category indicator dot */}
                      <div
                        className="w-2 h-2 rounded-full shadow-sm"
                        style={{ backgroundColor: barWidth > 30 ? 'rgba(255,255,255,0.8)' : color }}
                      />
                      <span
                        className={`text-sm font-semibold transition-all duration-200 ${
                          barWidth > 30
                            ? 'text-white drop-shadow-md'
                            : 'text-gray-800 dark:text-gray-200'
                        }`}
                        style={{
                          textShadow: barWidth > 30 ? '0 2px 4px rgba(0,0,0,0.4)' : 'none',
                        }}
                      >
                        {segment.type}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-bold transition-all duration-200 ${
                          barWidth > 30
                            ? 'text-white drop-shadow-md'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                        style={{
                          textShadow: barWidth > 30 ? '0 2px 4px rgba(0,0,0,0.4)' : 'none',
                        }}
                      >
                        {segment.value.toLocaleString()}
                      </span>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full transition-all duration-200 ${
                          barWidth > 30
                            ? 'bg-white/20 text-white backdrop-blur-sm'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {segment.percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Brand New Tooltip with Perfect Arrow Positioning */}
      {tooltip && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y - 10,
          }}
        >
          {/* Tooltip positioned so arrow points to exact bar center */}
          <motion.div
            className="relative"
            style={{
              transform: 'translate(-50%, -100%)', // Center tooltip horizontally, position above
            }}
            initial={{ opacity: 0, scale: 0.9, y: 5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 5 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            {/* Tooltip Body */}
            <div className="bg-gray-900 text-white px-5 py-4 rounded-lg shadow-2xl border border-gray-700 min-w-[260px]">
              {/* Header with colored indicator */}
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: getTypeColor(tooltip.percentage) }}
                />
                <h4 className="font-semibold text-base text-white">{tooltip.type}</h4>
              </div>

              {/* Main Stats */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-300">
                    {tooltip.count.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-400">Cases</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-indigo-300">
                    {tooltip.percentage.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-400">Share</div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="border-t border-gray-700 pt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">Rank:</span>
                  <span className="text-white font-medium">
                    #{segments.findIndex(s => s.type === tooltip.type) + 1} of {segments.length}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">Volume:</span>
                  <span className="text-white font-medium">
                    {tooltip.percentage >= 50
                      ? 'Very High'
                      : tooltip.percentage >= 20
                        ? 'High'
                        : tooltip.percentage >= 5
                          ? 'Medium'
                          : 'Low'}
                  </span>
                </div>
              </div>
            </div>

            {/* Arrow pointing down to bar center */}
            <div
              className="absolute top-full left-1/2 transform -translate-x-1/2"
              style={{ marginTop: '-1px' }}
            >
              <div
                className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent"
                style={{ borderTopColor: '#1F2937' }}
              />
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default CaseTypeBarChart;
